/**
 * Copyright © 2026 Adnuntius AS.
 */
import * as _ from 'lodash';

import {LEGACY_USER_PROFILE_KEYS, LocalUserProfile} from '../../../session/local-user-profile';
import {AdnHttpService} from '../../../util/adn-http.service';
import {createAdnHttpResource, createAdnHttpResourceSender} from '../../../util/create-adn-http-resource';
import {AuthHttpInterceptorService} from '../../../auth/auth-http-interceptor.service';
import {ApiErrorHandlerService} from '../../../../app/common/directives/error/api-error-handler.service';

/** Angular DI token for the UserProfile HTTP resource (factory product). */
export class UserProfileResource {
}

function transformRequest(data: any): any {
  const transformed = _.cloneDeep(data);
  if (_.isPlainObject(transformed.profile)) {
    transformed.profile = _.mapValues(transformed.profile, (value: any) => _.isObject(value) ? JSON.stringify(value) : value);
  }
  return transformed;
}

function transformResponse(data: any): any {
  if (_.isPlainObject(_.get(data, 'profile'))) {
    data.profile = _.mapValues(data.profile, (value: any) => {
      if (!_.isString(value)) {
        return value;
      }
      try {
        return JSON.parse(value);
      } catch (_err) {
        return value;
      }
    });
  }
  return data;
}

export function createUserProfileResource(
  AdnHttp: AdnHttpService,
  authHttpInterceptor: AuthHttpInterceptorService,
  ApiErrorHandler: ApiErrorHandlerService,
  localUserProfile: LocalUserProfile
): any {
  const resource = createAdnHttpResource(
    createAdnHttpResourceSender(AdnHttp, authHttpInterceptor, ApiErrorHandler),
    'userprofiles',
    () => localUserProfile,
    'userProfile',
    {context: null},
    {transformRequest, transformResponse}
  );

  const originalSave = resource.save.bind(resource);
  resource.save = function(a1: any, a2: any, a3: any, a4: any) {
    let params = null;
    let data = null;
    let success = null;
    if (_.isFunction(a1)) {
      success = a1;
    } else if (_.isFunction(a2)) {
      data = a1;
      success = a2;
    } else if (_.isFunction(a3)) {
      params = a1;
      data = a2;
      success = a3;
    } else {
      data = a1;
    }

    const payload = _.cloneDeep(data || params || {});
    const forceApiSave = payload.forceApiSave === true;
    delete payload.forceApiSave;
    if (_.isObject(payload.profile)) {
      _.forEach(LEGACY_USER_PROFILE_KEYS, function(key) {
        payload.profile[key] = null;
      });
    }

    const shouldUseLocalOnlySave = localUserProfile.isLocalOnlyProfileUser() && !forceApiSave && Math.floor(Math.random() * 20) !== 0;

    if (shouldUseLocalOnlySave) {
      const savedUser = localUserProfile.saveProfile(payload.profile || {});
      const response = {
        id: payload.id || savedUser.userId,
        profile: _.cloneDeep(savedUser.profile)
      };

      (success || (() => {}))(response);
      return {$promise: Promise.resolve(response)};
    }

    if (_.isFunction(a1)) {
      return originalSave(a1, a2, a3, a4);
    }
    if (_.isFunction(a2)) {
      return originalSave(payload, a2, a3);
    }
    if (_.isFunction(a3)) {
      return originalSave(params, payload, a3, a4);
    }
    return originalSave(payload, a2);
  };

  return resource;
}

export const userProfileResourceProvider = {
  provide: UserProfileResource,
  useFactory: createUserProfileResource,
  deps: [AdnHttpService, AuthHttpInterceptorService, ApiErrorHandlerService, LocalUserProfile]
};

